import type { AddressData } from '~~/core/entities/Addresses';

import { AddressUtilsMultiCity } from '~/utils/AddressUtils';

/**
 * Composable para gerenciar regiões destacadas com suporte multi-cidade
 * Substitui o mapeamento hardcoded de regiões destacadas
 */
export function useHighlightedRegionsMultiCity() {
	/**
	 * Retorna regiões destacadas para uma cidade específica
	 * Se não houver configuração específica, retorna as primeiras 5 regiões da cidade
	 */
	function getHighlightedRegionsForCity(
		state: string,
		city: string
	): AddressData[] {
		// Primeiro, tenta buscar regiões configuradas especificamente para destacar
		const configuredRegions = getConfiguredHighlightedRegions(state, city);

		if (configuredRegions.length > 0) {
			return configuredRegions;
		}

		// Fallback: retorna as primeiras 5 regiões da cidade
		const cityRegions = AddressUtilsMultiCity.getRegionsByCity(state, city);
		return cityRegions.slice(0, 5);
	}

	/**
	 * Retorna regiões destacadas para uma macro-região específica
	 */
	function getHighlightedRegionsForMacroregion(
		state: string,
		macroregion: string
	): AddressData[] {
		const configuredRegions = getConfiguredHighlightedRegions(
			state,
			macroregion
		);

		if (configuredRegions.length > 0) {
			return configuredRegions;
		}

		// Fallback: retorna as primeiras 5 regiões da macro-região
		const macroregionRegions = AddressUtilsMultiCity.getRegionsByMacroregion(
			state,
			macroregion
		);
		return macroregionRegions.slice(0, 5);
	}

	function getHighlightedRegions(
		state: string,
		city?: string,
		macroregion?: string,
		excludeRegions: string[] = []
	): AddressData[] {
		if (city) {
			const regions = getHighlightedRegionsForCity(state, city);
			return regions.filter(region => !excludeRegions.includes(region.label));
		}

		return [];
	}

	/**
	 * Retorna regiões destacadas para um estado específico
	 */
	function getHighlightedRegionsForState(state: string): AddressData[] {
		const configuredRegions = getConfiguredHighlightedRegions(state, '');

		if (configuredRegions.length > 0) {
			return configuredRegions;
		}

		// Fallback: retorna as primeiras 10 regiões do estado
		const stateRegions = AddressUtilsMultiCity.getRegionsByState(state);
		return stateRegions.slice(0, 10);
	}

	/**
	 * Configurações específicas de regiões destacadas
	 * Pode ser expandido para incluir mais estados/cidades conforme necessário
	 */
	function getConfiguredHighlightedRegions(
		state: string,
		location: string
	): AddressData[] {
		const configurations: Record<string, Record<string, string[]>> = {
			SP: {
				'São Paulo': [
					'Itaim Bibi',
					'Jardim Paulista',
					'Vila Nova Conceição',
					'Jardim América',
					'Higienópolis'
				],
				'São José dos Campos': [
					'Jardim Esplanada',
					'Jardim Apolo',
					'Vila Ema',
					'Jardim Nova America',
					'Jardim Alvorada'
				]
			},
			PR: {
				Curitiba: [
					'Água Verde',
					'Batel',
					'Campo Comprido',
					'Ecoville',
					'Santa Felicidade'
				]
			}
		};

		const stateConfig = configurations[state];
		if (!stateConfig) return [];

		const locationConfig = stateConfig[location] || stateConfig[''] || [];

		let regions: AddressData[];
		if (location) {
			regions = AddressUtilsMultiCity.getRegionsByCity(state, location);
		} else {
			regions = AddressUtilsMultiCity.getRegionsByState(state);
		}

		return locationConfig
			.map(regionName =>
				regions.find(
					region =>
						region.label === regionName &&
						(location ? region.data.city === location : true)
				)
			)
			.filter((region): region is AddressData => region !== undefined);
	}

	/**
	 * Adiciona uma nova configuração de regiões destacadas
	 * Útil para adicionar novos estados/cidades sem alterar código
	 */
	function addHighlightedRegionsConfiguration(
		state: string,
		location: string,
		regionNames: string[]
	): void {
		// Esta função pode ser expandida para salvar configurações em um store ou API
		// Por enquanto, apenas documenta a necessidade
		// eslint-disable-next-line no-console
		console.log(
			`Adding highlighted regions configuration for ${state}/${location}:`,
			regionNames
		);
	}

	/**
	 * Retorna estatísticas das regiões destacadas
	 */
	function getHighlightedRegionsStats(): {
		totalConfigurations: number;
		statesWithConfigurations: string[];
		locationsWithConfigurations: Array<{
			state: string;
			location: string;
			count: number;
		}>;
	} {
		const configurations: Record<string, Record<string, string[]>> = {
			SP: {
				'São Paulo': [
					'Itaim Bibi',
					'Jardim Paulista',
					'Vila Nova Conceição',
					'Jardim América',
					'Higienópolis'
				],
				'São José dos Campos': [
					'Jardim Esplanada',
					'Jardim Apolo',
					'Vila Ema',
					'Jardim Nova America',
					'Jardim Alvorada'
				],
				'': [
					'Itaim Bibi',
					'Jardim Paulista',
					'Vila Nova Conceição',
					'Jardim América',
					'Higienópolis'
				]
			},
			PR: {
				Curitiba: [
					'Água Verde',
					'Batel',
					'Campo Comprido',
					'Ecoville',
					'Santa Felicidade'
				],
				'': [
					'Água Verde',
					'Batel',
					'Campo Comprido',
					'Ecoville',
					'Santa Felicidade'
				]
			}
		};

		const statesWithConfigurations = Object.keys(configurations);
		const locationsWithConfigurations = statesWithConfigurations.flatMap(
			state => {
				const stateConfig = configurations[state];
				if (!stateConfig) return [];

				return Object.keys(stateConfig).map(location => {
					const locationData = stateConfig[location];
					return {
						state,
						location: location || 'Estado',
						count: locationData ? locationData.length : 0
					};
				});
			}
		);

		return {
			totalConfigurations: locationsWithConfigurations.length,
			statesWithConfigurations,
			locationsWithConfigurations
		};
	}

	return {
		getHighlightedRegionsForCity,
		getHighlightedRegionsForMacroregion,
		getHighlightedRegions,
		getHighlightedRegionsForState,
		addHighlightedRegionsConfiguration,
		getHighlightedRegionsStats
	};
}
